import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment-timezone';

import { setTrackParams } from '../app/Track/track.slice';
import { getUser } from '../services/AuthService';
import { resolveViewerTimezone } from '../utils/viewerTimezone';

import { setAppearance } from './basic.slice';
import { RootState } from './store';

export interface WorkflowSteps {
    WATCH_PLATFORM_OVERVIEW: boolean;
    INVITE_TEAM_MEMBER: boolean;
    CREATE_FIRST_QUOTE: boolean;
    BOOK_FIRST_ORDER: boolean;
    ENABLE_TEXT_NOTIFICATIONS: boolean;
}

interface AuthState {
    isAuthenticated: boolean;
    isUpdateProfile: boolean;
    currentUser: any;
    isMainUser: boolean;
    loader: boolean;
    timezone: string;
    shouldRedirectToLogin: boolean;
    workFlowSteps: WorkflowSteps | null;
    /** Resolved 3-tier order portal visibility (entity → customer → global → defaults). Set on app load. */
    portalVisibility: Record<string, boolean> | null;
}

const initialState: AuthState = {
    isAuthenticated: false,
    isUpdateProfile: false,
    currentUser: null,
    isMainUser: false,
    loader: true,
    timezone: 'UTC',
    shouldRedirectToLogin: false,
    workFlowSteps: null,
    portalVisibility: null,
};

// Async thunk for getting user data
export const fetchUserData = createAsyncThunk('auth/fetchUser', async (_, { dispatch }) => {
    const response = await getUser();
    console.log('🚀 ~ fetchUserData response:', response);

    // Appearance row (organisation clock) plus the user's stored timezone; the slice resolves the
    // READER's clock from them (user, browser, organisation, UTC), see utils/viewerTimezone.ts.
    const viewerTimezone = resolveViewerTimezone({ userTimezone: response?.data?.timezone, orgTimezone: response?.data?.appearanceData?.timezone });

    if (response?.data?.appearanceData) {
        dispatch(setAppearance({ ...response.data.appearanceData, viewerTimezone: response?.data?.timezone }));
    }

    // Radar's default day is "today" on the reader's clock
    const today = moment().tz(viewerTimezone).format('YYYY-MM-DD');
    dispatch(setTrackParams({ startDate: today, endDate: today }));

    return response;
});

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setIsAuthenticated: (state, action) => {
            state.isAuthenticated = action.payload;
        },
        setIsUpdateProfile: (state, action) => {
            state.isUpdateProfile = action.payload;
        },
        setCurrentUser: (state, action) => {
            state.currentUser = action.payload;
        },
        setLoader: (state, action) => {
            state.loader = action.payload;
        },
        setTimezone: (state, action) => {
            state.timezone = action.payload;
        },
        resetAuth: (state) => {
            state.loader = false;
            state.isAuthenticated = false;
            state.isUpdateProfile = false;
            state.currentUser = null;
            state.timezone = 'UTC';
            state.shouldRedirectToLogin = false;
            state.isMainUser = false;
            state.workFlowSteps = null;
        },
        clearRedirectToLogin: (state) => {
            state.shouldRedirectToLogin = false;
        },
        setWorkflowSteps: (state, action) => {
            state.workFlowSteps = action.payload;
        },
        setPortalVisibility: (state, action) => {
            state.portalVisibility = action.payload;
        },
        setUpdateOrganizationInfo: (state, action) => {
            state.currentUser = {
                ...state.currentUser,
                organization: { ...state.currentUser?.organization, ...action.payload },
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchUserData
            .addCase(fetchUserData.fulfilled, (state, action) => {
                if (action.payload?.data) {
                    if (!action.payload.data.isActive) {
                        localStorage.clear();
                        state.isAuthenticated = false;
                        state.shouldRedirectToLogin = true;
                    } else {
                        state.currentUser = action.payload.data;
                        state.workFlowSteps = action.payload.data?.workFlowSteps || null;
                        state.portalVisibility = action.payload.data?.portalVisibility ?? null;
                        state.isAuthenticated = true;
                        state.timezone = action.payload.data?.organization?.timezone || 'UTC';
                    }
                }
                state.isUpdateProfile = false;
                state.loader = false;
            })
            .addCase(fetchUserData.rejected, (state) => {
                state.loader = false;
                state.isAuthenticated = false;
                state.isUpdateProfile = false;
            });
    },
});

export const { setIsAuthenticated, setIsUpdateProfile, setCurrentUser, setLoader, resetAuth, setTimezone, clearRedirectToLogin, setUpdateOrganizationInfo, setWorkflowSteps, setPortalVisibility } =
    authSlice.actions;

export const currentUser = (state: RootState) => state.auth.currentUser;

export default authSlice.reducer;
